
import {
  Mixins,
  Component,
  Provide,
  Prop,
  Watch
} from "vue-property-decorator";
import { mapState } from "vuex";
import hash from "object-hash";
import _ from "@/boot/lodash";
import { CreateElement } from "vue";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ConfirmModalModes } from "@/data/enums";
import { Contexts } from "@/models/contexts";
import { BrandConfigKeys } from "@/data/constants";

// Types
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import { IState } from "@/store";
import { IBasket, IBasketPageForm } from "@/models/baskets";
import { IClient } from "@/models/clients";
import { IBrand, ICustomField } from "@/models/brands";
import { IUploadFile } from "@/models/uploadFiles";
import { ISocketMessage } from "@/models/socket";
import { IProductConfig } from "@/models/providers/products";
import { InvoiceCategoryCode } from "@/models/invoices";
import { IPricelist, IProduct, IProductCategory } from "@/models/products";
import { QUERY_PARAMS } from "@/data/constants";
import { IAccount } from "@/models/accounts";
import { IInvoice } from "@/models/invoices";
import { IOrder } from "@/models/orders";
import { IWarningNote } from "@/models/baskets";
import { ICurrency } from "@/models/constants";
import { IPromotion } from "@/models/promotions";
import { IProvider } from "@/models/providers";

@Component({
  computed: {
    ...mapState({
      basket(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.BASKETS
        });
      },
      brandSettingsScope(state: IState, getters) {
        return getters[`data/${DataModules.BRANDS}/brandScope`](this.brandId);
      },
      brandSettingsData(state: IState, getters) {
        if (this.brandId === state.brand.id) return state.brand;
        return (
          getters[`data/single`]({
            storeModule: DataModules.BRANDS,
            scope: this.brandSettingsScope
          }) || {}
        );
      },
      brandConfigurationScope(state: IState, getters) {
        return getters[`data/${DataModules.BRANDS}/configurationScope`](
          this.brandId
        );
      },
      brandConfigurationData(state: IState, getters) {
        return (
          getters[`data/single`]({
            storeModule: DataModules.BRANDS,
            scope: this.brandConfigurationScope
          }) || {}
        );
      },
      clientDataScope(state: IState, getters) {
        return getters[`data/${DataModules.CLIENTS}/scope`](this.clientId);
      },
      clientData(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CLIENTS,
          scope: this.clientDataScope
        });
      },
      currentBrandId(state: IState) {
        // The current brand context
        return state.brand.id;
      },
      requiredCustomFieldsScope(state: IState, getters) {
        return getters[`data/${DataModules.CUSTOM_FIELDS_BASKET}/scope`](
          this.basketId
        );
      },
      requiredCustomFields(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.CUSTOM_FIELDS_BASKET,
          scope: this.requiredCustomFieldsScope
        });
      },
      currencies: (state: IState) => state.constants.currencies
    })
  }
})
export default class BasketProvider extends Mixins(RequestPromises, Context)
  implements IProvider {
  // Props
  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({
    default: () => (vm: BasketProvider) => {
      return vm.$router.push({ name: "adminBaskets" }).catch(err => err);
    }
  })
  public onNotFound!: IProvider["onNotFound"];

  // State
  public basket!: IBasket;
  public currentBrandId!: IBrand["id"];

  public clientData!: IClient;
  public clientDataScope!: string;

  public requiredCustomFields!: ICustomField[];
  public requiredCustomFieldsScope!: string;

  public brandSettingsData!: IBrand;
  public brandSettingsScope!: string;

  public brandConfigurationData!: IBrand;
  public brandConfigurationScope!: string;

  public currencies!: ICurrency[];

  // Data

  public basketForm: IBasketPageForm = {
    custom_fields: {},
    notes: ""
  };
  public basketFormClone: IBasketPageForm = { custom_fields: {}, notes: "" };
  public basketIsProcessing = false;
  public clientId = "";
  public currencyIsSwitching = false;
  public customImages: IUploadFile[] = [];
  public hasRequestError = false;
  public isLoading = false;
  public isReloading = false;
  public shopRoute = "adminOrderShop";
  public currencyCode = "GBP";

  // Provide

  @Provide()
  public providerData(): IBasketProviderData {
    return {
      account: this.account,
      accountId: this.accountId,
      basket: this.basket,
      basketBrandId: this.basketBrandId,
      basketForm: this.basketForm,
      basketFormClone: this.basketFormClone,
      basketHash: this.basketHash,
      basketId: this.basketId,
      basketIsComplete: this.basketIsComplete,
      basketIsProcessing: this.basketIsProcessing,
      billingTerm: this.billingTerm,
      brandData: this.brandData,
      brandTaxType: this.brandTaxType,
      canAdjustTotal: this.canAdjustTotal,
      client: this.client,
      clientId: this.clientId,
      coupons: this.coupons,
      currencyCode: this.currencyCode,
      currencyId: this.currencyId,
      currencyIsSwitching: this.currencyIsSwitching,
      customImages: this.customImages,
      hasRequestError: this.hasRequestError,
      isGuestBasket: this.isGuestBasket,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      isValidCheckoutPage: this.isValidCheckoutPage,
      isVatRegistered: this.isVatRegistered,
      mainBasketLink: this.mainBasketLink,
      noRequiredCustomFieldsData: this.noRequiredCustomFieldsData,
      pathCrossSell: this.pathCrossSell,
      pathReadMore: this.pathReadMore,
      quantity: this.quantity,
      showVat: this.showVat,
      supportedCurrencies: this.supportedCurrencies
    };
  }

  @Provide()
  public get providerMethods(): IBasketProviderMethods {
    return {
      addPromotion: this.addPromotion,
      addToBasket: this.addToBasket,
      cancelOrder: this.cancelOrder,
      changeClient: this.changeClient,
      clearBasket: this.clearBasket,
      createAutoPromotion: this.createAutoPromotion,
      deleteProduct: this.deleteProduct,
      deletePromotion: this.deletePromotion,
      forceBasketReset: this.forceBasketReset,
      getBasket: this.getBasket,
      goToAuthenticate: this.goToAuthenticate,
      goToBasketView: this.goToBasketView,
      goToCheckout: this.goToCheckout,
      goToComplete: this.goToComplete,
      goToProductView: this.goToProductView,
      goToShop: this.goToShop,
      hideWarnings: this.hideWarnings,
      isConfigurable: this.isConfigurable,
      openCompleteRegistrationLogin: this.openCompleteRegistrationLogin,
      openLoginModal: this.openLoginModal,
      openPayInvoiceModal: this.openPayInvoiceModal,
      quickAddToBasket: this.quickAddToBasket,
      refreshBasketData: this.refreshBasketData,
      reloadData: this.reloadData,
      saveCustomImages: this.saveCustomImages,
      setAppForClient: this.setAppForClient,
      setIsProcessing: this.setIsProcessing,
      showCrossSellsModal: this.showCrossSellsModal,
      updateBasketData: this.updateBasketData,
      updateBasketProduct: this.updateBasketProduct,
      updateCurrency: this.updateCurrency
    };
  }

  // Life-cycle Methods

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $basketData: this.providerData(),
        $basketMethods: this.providerMethods
      })
    );
  }

  public async created() {
    this.isLoading = true;

    // Admin: If adding new order without account && client ids(means: basketKey is empty)
    //------------------------------------------
    if (this.isAdmin && this.isNewBasket && !this.basketKey) {
      return this.openSelectClientModal(() => this.backToBasketList());
    }
    await this.loadData();
    this.checkInitialRouting();
  }

  public async loadData() {
    try {
      this.isLoading = true;

      if (this.isClient || this.isGuest) {
        await this.getClientBasketData(this.isReloading);
      } else if (this.isNewBasket) {
        await this.getClientData(this.isReloading);
      } else {
        await this.getBasketData(this.isReloading);
      }

      await Promise.all([
        this.$store.dispatch("constants/getCurrencies"),
        this.$store.dispatch("constants/getBillingCycles"),
        this.getBrandSettings(this.isReloading),
        this.getBrandConfiguration(this.isReloading)
      ]);

      this.initialiseBasketPageForm();
      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
      this.setBasketRecalculatedEvent();

      if (
        this.basketIsComplete &&
        this.$router.currentRoute.name !== "adminOrderComplete" &&
        this.$router.currentRoute.name !== "orderCompleteStep"
      ) {
        this.goToComplete();
      }
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public initialiseBasketPageForm() {
    _.forEach(_.get(this.basket, "custom_fields", []), fieldObj => {
      this.$set(
        this.basketForm.custom_fields,
        fieldObj.field.code,
        fieldObj.value
      );
    });
    this.$set(this.basketForm, "notes", _.get(this.basket, "notes", ""));
    this.basketFormClone = _.cloneDeep(this.basketForm);

    this.setInitialCurrencyCode();
  }

  public checkInitialRouting() {
    if (this.isAdmin && _.get(this.$route, "name") === "adminOrderBasket") {
      this.$router.push({
        name: this.shopRoute
      });
    }
  }

  public async getBrandSettings(ignoreStored = false) {
    // Get brand settings

    if (this.brandId === this.$store.state.brand?.id) {
      await this.$store.dispatch("brand/get", { ignoreStored });
    } else {
      await this.$store.dispatch(
        `data/${DataModules.BRANDS}/getBrandSettings`,
        {
          id: this.isAdmin ? this.brandId : undefined,
          scope: this.brandSettingsScope,
          vm: this,
          ignoreStored
        }
      );
    }
    // Get brand config keys
    await this.$store.dispatch("brand/getConfig", {
      keys: [
        BrandConfigKeys.PRICE_DISPLAY_TYPE,
        BrandConfigKeys.SHOW_PROMOTIONS_ON_CATALOG,
        BrandConfigKeys.SHOW_PROMOTION_AS
      ],
      brandId: this.brandId
    });
  }

  public getBrandConfiguration(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.BRANDS}/getBrandConfiguration`,
      {
        params: {
          keys: [
            BrandConfigKeys.PAY_LATER_ENABLED,
            BrandConfigKeys.PARTIAL_PAYMENTS_ENABLED
          ].join(","),
          brand_id: this.isAdmin
            ? _.get(
                this.account,
                "brand_id",
                _.get(this.basket, "brand_id", undefined)
              )
            : undefined
        },
        scope: this.brandConfigurationScope,
        vm: this,
        ignoreStored
      }
    );
  }

  public get brandId() {
    return _.get(
      this.account,
      "brand_id",
      _.get(this.basket, "brand_id", this.$store.state.brand.id)
    );
  }

  public get brandData() {
    return _.merge({}, this.brandSettingsData, {
      config: this.brandConfigurationData
    });
  }

  public get brandTaxType() {
    return _.get(this.brandData, "tax_type", 0);
  }

  public get isVatRegistered() {
    return _.get(this.brandData, "vat_number", "").length > 0;
  }

  public get showVat() {
    return this.isVatRegistered && !_.get(this.brandData, "vat_exempt");
  }

  public get isValidCheckoutPage() {
    return (
      this.basket &&
      !!this.accountId &&
      _.get(this.basket, "products", []).length > 0 &&
      !this.basketIsComplete &&
      !this.noRequiredCustomFieldsData &&
      !this.isGuest
    );
  }

  public get canAdjustTotal() {
    return this.isAdmin && this.$userCan("basket_balance_promo");
  }

  public getClientData(ignoreStored = false) {
    this.clientId = _.get(this.$route, `query.${QUERY_PARAMS.CLIENT_ID}`, "");
    return this.$store.dispatch(`data/${DataModules.CLIENTS}/get`, {
      id: this.clientId,
      params: {
        with: `image,accounts.brand.image,accounts.pricelist,companies`
      },
      scope: this.clientDataScope,
      ignoreStored,
      vm: this
    });
  }

  public changeClient() {
    this.openSelectClientModal();
  }

  public get isNewBasket() {
    return this.basketId === "new";
  }

  public get basketHash() {
    return hash(this.basket);
  }

  public openSelectClientModal(onCancel: () => null | void = () => null) {
    return this.$store.dispatch(
      `data/${DataModules.CLIENTS}/openSelectClientModal`,
      {
        props: {
          allowGuestClientOption: true
        },
        events: {
          onSelect: async (client: IClient) => {
            const oldBasketKey = this.basketKey;
            await this.$router.replace(
              _.merge(
                this.$route.name === this.shopRoute
                  ? {}
                  : { name: this.shopRoute },
                {
                  query: {
                    [QUERY_PARAMS.ACCOUNT_ID]: _.get(client.accounts, "[0].id"),
                    [QUERY_PARAMS.CLIENT_ID]: client.id
                  }
                }
              )
            );
            if (!oldBasketKey) this.reInitialiseProvider(this.basketKey, true);
          }
        },
        onCancel
      }
    );
  }

  public backToBasketList(): void {
    this.$router.push({ name: "adminBaskets" });
  }

  public async forceBasketReset(
    accountId = this.accountId,
    clientId = this.clientId
  ) {
    this.destroyBasket();
    this.reinitiateVariables();
    this.goToShop(
      this.isAdmin
        ? {
            [QUERY_PARAMS.ACCOUNT_ID]: accountId,
            [QUERY_PARAMS.CLIENT_ID]: clientId
          }
        : {},
      {
        basketId: "new"
      }
    );
  }

  public goToShop(query = {}, params = {}) {
    this.$router.push({
      name: this.isAdmin ? "adminOrderShop" : "orderShopStep",
      query,
      params
    });
  }

  public goToProductView(
    basketProductId = "",
    productId = "",
    queryParams = {}
  ) {
    this.$router.push({
      name: this.isAdmin ? "adminOrderProductView" : "orderProductViewStep",
      query: _.merge(
        {},
        basketProductId
          ? { [QUERY_PARAMS.BASKET_PRODUCT_ID]: basketProductId }
          : {},
        productId ? { [QUERY_PARAMS.PRODUCT_ID]: productId } : {},
        queryParams
      )
    });
  }

  public goToCheckout() {
    this.$router.push({
      name: this.isAdmin ? "adminOrderCheckout" : "orderCheckoutStep"
    });
  }

  public goToAuthenticate() {
    this.$router.push({
      name: "orderAuthenticateStep"
    });
  }

  public goToBasketView(basketId) {
    this.$router
      .push({
        name: this.isAdmin ? "adminOrderBasketView" : "orderBasketStep",
        ...(basketId ? { params: { basketId } } : {})
      })
      .catch(err => err);
  }

  public goToComplete(query = {}) {
    this.$router.push(
      _.merge({
        name: this.isAdmin ? "adminOrderComplete" : "orderCompleteStep",
        query
      })
    );
  }

  public getQuery() {
    const query = _.clone(this.$route.query);
    const regex = new RegExp(
      `^(${[
        QUERY_PARAMS.ACCOUNT_ID,
        QUERY_PARAMS.CLIENT_ID,
        QUERY_PARAMS.PRODUCT_ID,
        QUERY_PARAMS.BASKET_PRODUCT_ID,
        QUERY_PARAMS.BILLING_CYCLE_MONTHS,
        QUERY_PARAMS.QUANTITY,
        QUERY_PARAMS.CURRENCY_CODE
      ].join("|")})$`,
      "gm"
    );
    const matches = _.keys(query)
      .join("\n")
      .match(regex);
    if (!matches || !matches.length) return;
    matches.forEach(key => delete query[key]);
    return query;
  }

  public async getBasketData(ignoreStored = false) {
    try {
      await this.getBasket(ignoreStored);
      this.clientId = _.get(this.basket, "client_id");
    } catch (error) {
      if (this.$store.getters["api/is404"](error)) {
        this.onNotFound && this.onNotFound(this);
        throw this.$t("_sentence.basket_no_longer_exists");
      }
      throw error;
    }
  }

  public get currentCustomFields() {
    const customFields = { custom_fields: {} };
    _.forEach(_.get(this.basket, "custom_fields", []), fieldObj => {
      this.$set(
        customFields.custom_fields,
        fieldObj.field.code,
        fieldObj.value
      );
    });

    return customFields;
  }

  public get coupons() {
    const basketPromos = _.get(this.basket, "promotions", []);
    const basketCoupons = _.map(basketPromos, promo => promo.promotion.code);
    let queryCoupons = _.get(this.$route, `query.${QUERY_PARAMS.COUPONS}`, "");
    queryCoupons = (_.isArray(queryCoupons)
      ? queryCoupons.join(",")
      : queryCoupons
    ).split(",");
    const mergedCoupons = _.compact(_.union(basketCoupons, queryCoupons));
    return _.uniq(mergedCoupons);
  }

  public get supportedCurrencies() {
    return _.orderBy(this.brandData?.currencies, ["name", "asc"]);
  }

  public get pathCurrencyCode(): ICurrency["code"] {
    return _.get(this.$route.query, QUERY_PARAMS.CURRENCY_CODE, "")
      .toString()
      .toUpperCase() as ICurrency["code"];
  }

  public get currencyId(): IBasketProviderData["currencyId"] {
    return _.get(
      _.find(this.currencies, ["code", this.currencyCode]),
      "id"
    ) as ICurrency["id"];
  }

  public setInitialCurrencyCode() {
    const basketCode = this.basket?.currency?.code;
    const brandCurrencies = this.supportedCurrencies;
    const brandCodes = _.map(brandCurrencies, "code");
    const brandCode = _.find(brandCurrencies, [
      "id",
      this.brandData?.currency_id
    ])?.code;
    const accountCode = _.find(brandCurrencies, [
      "id",
      this.account?.currency_id
    ])?.code;

    // 1st source – use basket.currency_id
    if (basketCode) return (this.currencyCode = basketCode);

    // 2nd source – use query.currId
    if (this.pathCurrencyCode && brandCodes.includes(this.pathCurrencyCode))
      return (this.currencyCode = this.pathCurrencyCode);

    // 3rd source – use account.currency_id
    if (accountCode) return (this.currencyCode = accountCode);

    // 4th source – use brand.currency_id
    if (brandCode) return (this.currencyCode = brandCode);

    // 5th source – take first supported currency code
    this.currencyCode = brandCodes[0];
  }

  public get billingTerm(): number {
    const queryBCM = _.toNumber(
      this.$route.query[QUERY_PARAMS.BILLING_CYCLE_MONTHS]
    );
    if (queryBCM) return queryBCM;
    return 0;
  }

  public get quantity(): number {
    const queryQty = _.toNumber(this.$route.query[QUERY_PARAMS.QUANTITY]);
    if (queryQty) return queryQty;
    return 1;
  }

  public async updateCurrency(
    currencyCode: IBasketProviderData["currencyCode"]
  ) {
    const basketCurrencyCode = _.get(this.basket, "currency.code");
    const supportedCCodes = _.map(this.supportedCurrencies, "code");

    // Abort if currencyCode is missing or unchanged
    if (!currencyCode || currencyCode === basketCurrencyCode) return;

    // Abort if currencyCode is not supported
    if (!supportedCCodes.includes(currencyCode)) return;

    // Abort if currencyCode is already selected
    if (currencyCode === this.pathCurrencyCode) return;

    this.currencyCode = currencyCode;

    if (!basketCurrencyCode)
      return this.$router.push({
        query: _.merge({}, this.$route.query, {
          [QUERY_PARAMS.CURRENCY_CODE]: currencyCode
        })
      });

    try {
      this.currencyIsSwitching = true;
      await this.$store.dispatch(`data/${DataModules.BASKETS}/updateCurrency`, {
        id: this.basketId,
        isClient: !this.isAdmin,
        data: { currency_code: currencyCode }
      });
      await this.refreshBasketData();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.currencyIsSwitching = false;
    }
  }

  public async openPayInvoiceModal(config) {
    const modal = await this.$store.dispatch("ui/open/windowModal", {
      config: _.merge(
        {
          component: () =>
            import("@/components/app/global/payments/invoicePaymentModal.vue"),
          width: 740,
          events: {
            success: () => {
              modal.close();
              this.$emit("paid");
            }
          }
        },
        config
      )
    });
  }

  public setIsProcessing(value: boolean) {
    this.basketIsProcessing = value;
  }

  public async showCrossSellsModal(
    productId: IProduct["id"],
    isProvisionable: Function
  ) {
    let results = { total: 0, data: [] };
    this.setIsProcessing(true);

    results = await this.getProductCrossSells(productId);
    this.setIsProcessing(false);

    if (!results.total) return;

    const modal = await this.$store.dispatch("ui/open/windowModal", {
      config: {
        component: () =>
          import(
            "@/components/app/global/baskets/basket/crossSell/crossSellsModal.vue"
          ),
        canCancel: ["escape", "outside", "x"],
        width: 1080,
        parent: this,
        props: {
          productId,
          basketId: this.basketId,
          accountId: this.accountId,
          currencyId: this.currencyId,
          coupons: this.coupons,
          getCrossSellsData: this.getProductCrossSells,
          results: _.pick(results, ["total", "data"])
        },
        events: {
          close: () => {
            modal.close();
          },
          viewCategory: (categoryId: IProductCategory["id"]) => {
            this.goToShop({ [QUERY_PARAMS.CATEGORY_ID]: categoryId });
            modal.close();
          },
          addToBasket: async (product: IProduct) => {
            // If configurable or provisionable
            if (
              this.isConfigurable(product) ||
              (await isProvisionable(product.id, this.clientId))
            ) {
              const currentQueryParams = this.$route.query;
              this.goToProductView(
                "",
                product.id,
                _.merge({}, currentQueryParams, {
                  [QUERY_PARAMS.CROSS_SELL_PRODUCT]: 1
                })
              );
            } else {
              // Else, quick add to basket
              await this.quickAddToBasket(product);
            }
            modal.close();
          }
        }
      }
    });
  }

  public async getProductCrossSells(
    productId: IProduct["id"],
    limit = 4,
    offset = 0
  ) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/getCrossSells`, {
      id: productId,
      returnData: false,
      storeData: false,
      params: {
        limit,
        offset,
        basket_id: this.basketId,
        ...(this.accountId ? { account_id: this.accountId } : {}),
        currency_id: this.currencyId,
        ...(this.coupons ? { promotions: this.coupons.join() } : {}),
        omit_basket_products: 1,
        "filter[active]": true,
        order: "order",
        with: [
          "image",
          "prices",
          "products_attributes",
          "products_options",
          "products_options.prices"
        ].join()
      },
      vm: this
    });
  }

  public isConfigurable(product: IProduct): boolean {
    // Check prices
    const availablePrices = _.get(product, "prices", []);
    if (availablePrices.length > 1) return true;

    // Check options and attributes
    const usedBillingTerm = _.get(
      availablePrices,
      "[0].billing_cycle_months",
      null
    );
    const availableOptions = _.filter(
      _.get(product, "options", _.get(product, "products_options", [])),
      availableOption => {
        return !!_.find(availableOption.prices, price =>
          [0, usedBillingTerm].includes(price.billing_cycle_months)
        );
      }
    );
    const availableAttributes = _.get(
      product,
      "attributes",
      _.get(product, "products_attributes", [])
    );
    return !!availableOptions.length || !!availableAttributes.length;
  }

  public async getBasket(ignoreStored = false, basketId = this.basketId) {
    try {
      this.basketIsProcessing = true;
      await this.$store.dispatch(`data/${DataModules.BASKETS}/get`, {
        id: basketId,
        isClient: !this.isAdmin,
        vm: this,
        ignoreStored
      });
    } finally {
      this.basketIsProcessing = false;
    }
  }

  public async getClientBasketData(ignoreStored = false) {
    if (this.pathBasketId || this.isAdmin) {
      await this.getBasket(true, this.pathBasketId);
    } else {
      await this.$store.dispatch(`data/${DataModules.BASKETS}/currentBasket`, {
        vm: this,
        ignoreStored
      });
    }
    this.clientId = _.get(
      this.basket,
      "client_id",
      (!this.isAdmin && _.get(this.$store.state, "user.id")) || this.clientId
    );
  }

  public get pathOrderId() {
    return this.$route.query[QUERY_PARAMS.ORDER_ID];
  }

  public get pathCrossSell() {
    return !!_.toNumber(
      _.get(this.$route.query, QUERY_PARAMS.CROSS_SELL_PRODUCT, null)
    );
  }

  public get pathReadMore() {
    return !!_.toNumber(_.get(this.$route.query, QUERY_PARAMS.READ_MORE, null));
  }

  public refreshBasketData() {
    if (this.pathOrderId || this.basketId) {
      return this.getBasket(
        true,
        this.pathOrderId ? (this.pathOrderId as IOrder["id"]) : this.basketId
      );
    }

    return this.$store.dispatch(`data/${DataModules.BASKETS}/currentBasket`, {
      vm: this,
      ignoreStored: true
    });
  }

  public async hideWarnings(warningIds: IWarningNote["id"][]) {
    await this.$store.dispatch(`data/${DataModules.BASKETS}/hideWarnings`, {
      vm: this,
      id: this.basketId,
      warningIds
    });
    await this.getBasket(true);
  }

  public async openLoginModal() {
    const modal = await this.$store.dispatch("auth/openLoginModal", {
      events: {
        "logged-in": async () => {
          await this.setAppForClient();
          modal.close();
        }
      }
    });
  }

  public async setAppForClient() {
    await this.$store.commit("context", Contexts.CLIENT);
    await this.$store.dispatch("auth/client/prepare");
    await this.getClientBasketData();
    this.goToCheckout();
  }

  public async cancelOrder() {
    if (!this.basket) {
      return this.$router.push({ name: "adminBaskets" });
    }
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          mode: ConfirmModalModes.BUTTON,
          confirmButtonText: this.$t("_action.cancel_order"),
          closeButtonText: this.$t("_action.close"),
          message: this.$t("_sentence.cancel_object_confirmation", {
            object: (this.$t("_.order") as string).toLowerCase()
          })
        },
        events: {
          confirmed: async () => {
            await this.$store.dispatch(`data/${DataModules.BASKETS}/remove`, {
              id: this.basketId,
              vm: this
            });
            confirm.close();
            return this.$router.push({ name: "adminBaskets" });
          }
        }
      }
    });
  }

  public async openCompleteRegistrationLogin() {
    const modal = await this.$store.dispatch(
      "auth/openCompleteRegistrationModal",
      {
        events: {
          "registration-completed": async () => {
            await this.$store.dispatch("auth/client/prepare");
            await this.refreshBasketData();
            modal.close();
          }
        }
      }
    );
  }

  public get basketId(): IBasket["id"] {
    if (this.basket) {
      return _.get(this.basket, "id");
    }

    if (this.isAdmin && _.has(this.$route, "params.basketId")) {
      return _.get(this.$route, "params.basketId") !== "new"
        ? _.get(this.$route, "params.basketId")
        : "new";
    }

    return "new";
  }

  public get accountId(): IAccount["id"] {
    if (this.basket) return _.get(this.basket, "account.id", "");
    return _.get(this.$route, `query.${QUERY_PARAMS.ACCOUNT_ID}`, "");
  }

  public get basketBrandId() {
    // Check
    return _.get(this.client, "brand_id", this.$store.state.brand.id);
  }

  public get pathBasketId() {
    return _.get(
      this.$route.query,
      QUERY_PARAMS.BASKET_ID,
      ""
    ) as IBasket["id"];
  }

  public get basketPricelistId(): IPricelist["id"] {
    return _.get(
      this.account,
      "pricelist_id",
      _.get(this.$store, "state.brand.pricelist_id", "")
    );
  }

  public get mainBasketLink() {
    if (this.isAdmin) {
      if (this.basketIsComplete) return { name: "adminOrderComplete" };
      return _.merge(
        this.$route.name === this.shopRoute ? {} : { name: this.shopRoute },
        {
          query: this.$route.query
        }
      );
    }

    return { name: "clientDashboard" };
  }

  public get account() {
    if (this.isClient) {
      return this.$store.getters["auth/client/account"];
    }

    if (this.basket) {
      return _.get(this.basket, "account");
    }

    if (this.clientData) {
      return _.find(this.client.accounts, ["id", this.accountId]);
    }

    return {};
  }

  public get client() {
    if (this.basket) {
      return _.get(this.basket, "client");
    }

    if (this.isClient) {
      return this.$store.state.user;
    }

    if (this.clientData) {
      return this.clientData;
    }

    return {};
  }

  public setBasketRecalculatedEvent() {
    this.$bus.$off("socket-basket-recalculated");
    this.$bus.$on(
      "socket-basket-recalculated",
      (socketMessage: ISocketMessage) => {
        const data = JSON.parse(socketMessage.message);
        if (data.invoice_id !== this.basketId) {
          return;
        }
        this.getBasket(true);
      }
    );
  }

  public get differentBrandError() {
    return (
      this.basket &&
      this.currentBrandId &&
      this.currentBrandId !== this.basket.brand_id
    );
  }

  public get basketIsComplete() {
    return !_.isEmpty(_.get(this.basket, "number", {}));
  }

  public get isGuestBasket() {
    return !_.isEmpty(this.basket) && _.isEmpty(this.basket.account);
  }

  public get basketKey() {
    const accountId =
      this.$route.query[QUERY_PARAMS.ACCOUNT_ID] ||
      _.get(this.basket, "account_id");
    const clientId =
      this.$route.query[QUERY_PARAMS.CLIENT_ID] ||
      _.get(this.basket, "client_id");
    if (accountId && clientId) return btoa([accountId, clientId].join(":"));
    return null;
  }

  public async clearBasket() {
    this.destroyBasket();
    this.reinitiateVariables();
  }

  public reinitiateVariables() {
    this.customImages = [];
    this.basketForm = {
      custom_fields: {},
      notes: ""
    };
    this.basketFormClone = { custom_fields: {}, notes: "" };
  }

  public async addToBasket(product: IProductConfig, showToast = false) {
    try {
      this.basketIsProcessing = true;
      const basket: IBasket =
        this.basketId === "new"
          ? await this.createBasketWithProduct(product)
          : await this.$store.dispatch(
              `data/${DataModules.BASKETS}/addProduct`,
              {
                id: this.basketId,
                isClient: !this.isAdmin,
                product
              }
            );
      await this.getBasket(true, basket.id);
      if (showToast) {
        this.$store.dispatch("toast/show", {
          message: this.$t("_.product_successfully_added_to_the_basket")
        });
      }
      return basket;
    } finally {
      this.basketIsProcessing = false;
    }
  }

  public async quickAddToBasket(product: IProduct) {
    const basket = await this.addToBasket(
      this.computeProductConfig(product),
      true
    );
    this.goToBasketView(basket.id);
  }

  public computeProductConfig(product: IProduct) {
    return {
      attributes: [],
      options: [],
      billing_cycle_months: _.get(product, "prices[0].billing_cycle_months", 1),
      product_id: product.id,
      promotions: _.map(this.coupons, promocode => ({
        promocode
      })),
      quantity: _.get(product, "unit_quantity") || 1
    };
  }

  public async createBasketWithProduct(product: IProductConfig) {
    const basket: IBasket = await this.$store.dispatch(
      `data/${DataModules.BASKETS}/create`,
      {
        data: {
          category_slug: InvoiceCategoryCode.NEW_CONTRACT, // TODO - wait on BE
          client_id: this.clientId,
          currency_code: this.currencyCode,
          account_id: this.account.id,
          pricelist_id: this.basketPricelistId,
          products: [_.omit(product, ["promotions"])],
          ...(_.pick(product, "promotions") || {})
        },
        isClient: !this.isAdmin
      }
    );
    // Update clientId
    this.$set(this, "clientId", basket.client_id);
    // Return basket
    return basket;
  }

  public async updateBasketData(data) {
    try {
      this.basketIsProcessing = true;
      await this.$store.dispatch(`data/${DataModules.BASKETS}/update`, {
        id: this.basketId,
        isClient: !this.isAdmin,
        data
      });
    } finally {
      this.basketIsProcessing = false;
    }
  }

  public async updateBasketProduct(
    basketProductId,
    productConfig: IProductConfig
  ) {
    try {
      this.basketIsProcessing = true;
      const basket: IBasket = await this.$store.dispatch(
        `data/${DataModules.BASKETS}/updateProduct`,
        {
          isClient: !this.isAdmin,
          basketId: this.basketId,
          basketProductId,
          productConfig
        }
      );
      await this.getBasket(true, basket.id);
      return basket;
    } finally {
      this.basketIsProcessing = false;
    }
  }

  public async deleteProduct(
    productId: IProduct["id"],
    invoiceId: IInvoice["id"]
  ) {
    const basket: IBasket = await this.$store.dispatch(
      `data/${DataModules.BASKETS}/deleteProduct`,
      {
        id: invoiceId,
        isClient: !this.isAdmin,
        basketProductId: productId
      }
    );
    await this.getBasket(true, basket.id);
    return basket;
  }

  public deletePromotion(promotionId: IPromotion["id"]) {
    return this.$store.dispatch(`data/${DataModules.BASKETS}/deletePromotion`, {
      id: this.basketId,
      isClient: !this.isAdmin,
      promotionId
    });
  }

  public addPromotion(promocode: string) {
    return this.$store.dispatch(`data/${DataModules.BASKETS}/addPromotion`, {
      id: this.basketId,
      isClient: !this.isAdmin,
      promocode
    });
  }

  public createAutoPromotion(total: number | string) {
    if (total === "") return Promise.resolve();

    return this.$store.dispatch(
      `data/${DataModules.BASKETS}/createAutoPromotion`,
      {
        id: this.basketId,
        isClient: !this.isAdmin,
        total
      }
    );
  }

  public saveCustomImages(customImages: IUploadFile[]) {
    this.customImages = customImages;
  }

  public get noRequiredCustomFieldsData() {
    const captured = _.get(this.basket, "custom_fields");
    const values = _.mapValues(_.keyBy(captured, "field_id"), "value");
    return _.some(this.requiredCustomFields, field =>
      _.isNil(values[field.id])
    );
  }

  public getRequiredCustomFields() {
    return this.$store.dispatch(
      `data/${DataModules.CUSTOM_FIELDS_BASKET}/list`,
      {
        scope: this.requiredCustomFieldsScope,
        params: {
          brand_id: this.basketBrandId,
          limit: 0,
          "filter[required]": 1,
          order: "order"
        },
        vm: this
      }
    );
  }

  @Watch("basketKey")
  public reInitialiseProvider(newKey, oldKey) {
    if (!oldKey) return;
    this.clearBasket();
    this.loadData();
  }

  @Watch("$route", { deep: true, immediate: true })
  public checkRoute() {
    if (
      this.basketId !== "new" &&
      (_.get(this.$route.query, QUERY_PARAMS.ACCOUNT_ID, "") ||
        _.get(this.$route.query, QUERY_PARAMS.CLIENT_ID, "") ||
        _.get(this.$route.query, QUERY_PARAMS.CURRENCY_CODE, ""))
    ) {
      this.$router.replace({
        query: this.getQuery()
      });
    }
  }

  @Watch("basket")
  public onBasketChange() {
    if (this.isAdmin && this.differentBrandError) {
      this.backToBasketList();
      this.$store.dispatch("toast/error", {
        message: this.$t("_sentence.this_basket_is_from_different_brand")
      });
    }

    if (this.basketId) {
      this.getRequiredCustomFields();
    }
  }

  public destroyClient() {
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.CLIENTS,
      scope: this.clientDataScope,
      vm: this
    });
  }

  public destroyBasket() {
    this.destroyClient();

    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.BASKETS,
      ignoreDependants: true
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.CUSTOM_FIELDS_BASKET,
      vm: this
    });

    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.BRANDS,
      scope: this.brandSettingsScope,
      vm: this
    });

    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.BRANDS,
      scope: this.brandConfigurationScope,
      vm: this
    });
  }

  public beforeDestroy() {
    this.destroyBasket();
    this.$bus.$off("socket-basket-recalculated");
  }
}
